import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import type { OptionsChain } from '../types/finance'
import { getOptionsChain, isUsingMockData } from '../lib/finance-api'
import OptionsTable from '../components/finance/OptionsTable'
import MockDataBanner from '../components/finance/MockDataBanner'

export default function OptionsChainPage() {
  const { symbol } = useParams<{ symbol: string }>()
  const [chain, setChain] = useState<OptionsChain | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedExpiration, setSelectedExpiration] = useState<string>('')
  const [activeTab, setActiveTab] = useState<'calls' | 'puts' | 'both'>('both')
  const [isMock, setIsMock] = useState(false)

  useEffect(() => {
    if (!symbol) return
    loadChain(symbol)
  }, [symbol])

  async function loadChain(sym: string, expiration?: string) {
    try {
      setLoading(true)
      setError(null)
      const data = await getOptionsChain(sym, expiration)
      setChain(data)
      setIsMock(isUsingMockData())
      if (!selectedExpiration) setSelectedExpiration(data.selectedExpiration)
    } catch {
      setError(`Failed to load options chain for ${sym}`)
    } finally {
      setLoading(false)
    }
  }

  function handleExpirationChange(exp: string) {
    setSelectedExpiration(exp)
    if (symbol) loadChain(symbol, exp)
  }

  if (loading && !chain) {
    return (
      <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-6 animate-pulse">
        <div className="h-8 bg-slate-200 dark:bg-slate-700 rounded w-1/3 mb-6" />
        <div className="space-y-2">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <div key={i} className="h-10 bg-slate-200 dark:bg-slate-700 rounded" />
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 text-red-600 dark:text-red-400 rounded-xl px-4 py-3 text-sm">
        {error}
      </div>
    )
  }

  if (!chain) return null

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Link to="/dashboard/financial-app" className="text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </Link>
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{chain.symbol} Options</h1>
            <span className="text-slate-500 dark:text-slate-400 text-sm">Underlying: ${chain.underlyingPrice.toFixed(2)}</span>
          </div>
          <p className="text-slate-500 dark:text-slate-400 text-sm">Options Chain</p>
        </div>
      </div>

      <MockDataBanner visible={isMock} />

      {/* Controls */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <label className="text-slate-500 dark:text-slate-400 text-sm">Expiration:</label>
          <select
            value={selectedExpiration}
            onChange={(e) => handleExpirationChange(e.target.value)}
            className="bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-white text-sm rounded-lg px-3 py-1.5 border border-slate-300 dark:border-slate-600 focus:border-blue-500 outline-none"
          >
            {chain.expirationDates.map((date) => (
              <option key={date} value={date}>
                {new Date(date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </option>
            ))}
          </select>
          {loading && <span className="text-slate-500 text-xs">Loading...</span>}
        </div>
        <div className="flex bg-slate-200 dark:bg-slate-700 rounded-lg p-0.5">
          {(['calls', 'puts', 'both'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors capitalize ${
                activeTab === tab
                  ? 'bg-slate-300 dark:bg-slate-600 text-slate-900 dark:text-white font-medium'
                  : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* Options tables */}
      {activeTab === 'both' ? (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 bg-emerald-500/5">
              <h3 className="text-emerald-600 dark:text-emerald-400 font-semibold text-sm">Calls</h3>
            </div>
            <OptionsTable contracts={chain.calls} type="calls" underlyingPrice={chain.underlyingPrice} />
          </div>
          <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 bg-red-500/5">
              <h3 className="text-red-600 dark:text-red-400 font-semibold text-sm">Puts</h3>
            </div>
            <OptionsTable contracts={chain.puts} type="puts" underlyingPrice={chain.underlyingPrice} />
          </div>
        </div>
      ) : (
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
          <div className={`px-4 py-3 border-b border-slate-200 dark:border-slate-700 ${activeTab === 'calls' ? 'bg-emerald-500/5' : 'bg-red-500/5'}`}>
            <h3 className={`font-semibold text-sm ${activeTab === 'calls' ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
              {activeTab === 'calls' ? 'Calls' : 'Puts'}
            </h3>
          </div>
          <OptionsTable
            contracts={activeTab === 'calls' ? chain.calls : chain.puts}
            type={activeTab}
            underlyingPrice={chain.underlyingPrice}
          />
        </div>
      )}

      {/* Link to fundamentals */}
      <div className="mt-8 text-center">
        <Link
          to={`/dashboard/financial-app/fundamentals/${chain.symbol}`}
          className="inline-flex items-center gap-2 px-6 py-3 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-900 dark:text-white text-sm font-medium rounded-xl transition-colors"
        >
          View Fundamentals
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </Link>
      </div>
    </div>
  )
}
